import { ReloadOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Select, Spin, message } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import CourseOption from '../../components/CourseOption';
import CourseAPI from '../../course/CourseAPI';
import Course from '../../course/types/Course';
import SubsidiaryCompanyAPI from '../../subsidiaryCompany/SubsidiaryCompanyAPI';
import SubsidiaryCompany from '../../subsidiaryCompany/types/SubsidiaryCompany';
import { getFullContactName } from '../utils/contactPerson';
import {
  getNumberListFromURLSearchParams,
  setNumberListInURLSearchParams,
} from '../../utils/searchParams';
import { SearchParamKeys } from '../views/BookingOrders';
import { Permission } from '../../user/types/Permission';
import { useUser } from '../../user/userContext';
import { userHasPermission } from '../../user/userUtils';
import useContactPersonOptions from '../hooks/useContactPersonOptions';

const { RangePicker } = DatePicker;

type FilterFormValues = {
  date?: [dayjs.Dayjs, dayjs.Dayjs];
  subsidiaryCompanyIds?: number[];
  courseIds?: number[];
  contactPersonIds?: number[];
};

type OpenClassBookingOrderFilterPickerProps = {
  className?: string;
  defaultSubsidiaryCompanyId?: number;
};

const OpenClassBookingOrderFilterPicker: React.FC<
  OpenClassBookingOrderFilterPickerProps
> = ({ className, defaultSubsidiaryCompanyId }) => {
  const [currentUser] = useUser();
  const { t } = useTranslation();
  const [form] = Form.useForm<FilterFormValues>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState<Course[]>([]);
  const [subsidiaryCompanies, setSubsidiaryCompanies] = useState<
    SubsidiaryCompany[]
  >([]);
  const canReadCourses = userHasPermission(
    currentUser,
    Permission.COURSE_READ_LIST,
  );
  const canReadContactPersons = userHasPermission(
    currentUser,
    Permission.CONTACT_PERSON_READ_LIST,
  );
  const canFilterSubsidiaryCompanies =
    userHasPermission(currentUser, Permission.SUBSIDIARY_COMPANY_READ_LIST) &&
    userHasPermission(currentUser, Permission.BOOKING_ORDER_READ_GLOBAL);
  const selectedContactPersonIds = useMemo(
    () =>
      getNumberListFromURLSearchParams(
        searchParams,
        SearchParamKeys.ContactPersonIds,
      ),
    [searchParams],
  );
  const {
    contactPersons,
    hasMoreContactPersons,
    loadFirstContactPersonPage,
    loadMoreContactPersons,
    loadingContactPersonOptions,
    loadingContactPersons,
    searchContactPersons,
  } = useContactPersonOptions({
    enabled: canReadContactPersons,
    hydrateSelectedIds: selectedContactPersonIds,
  });

  useEffect(() => {
    const loadFilterOptions = async () => {
      const [coursesResponse, subsidiariesResponse] = await Promise.all([
        canReadCourses ? CourseAPI.getCourses() : Promise.resolve(undefined),
        canFilterSubsidiaryCompanies
          ? SubsidiaryCompanyAPI.getSubsidiaryCompanies()
          : Promise.resolve(undefined),
      ]);

      setCourses(coursesResponse?.data ?? []);
      setSubsidiaryCompanies(subsidiariesResponse?.data ?? []);
    };

    loadFilterOptions().catch(() =>
      message.error(t('views.OpenClasses.loadFailed')),
    );
  }, [canFilterSubsidiaryCompanies, canReadCourses, t]);

  useEffect(() => {
    const from = searchParams.get(SearchParamKeys.From);
    const to = searchParams.get(SearchParamKeys.To);

    form.setFieldsValue({
      date: from && to ? [dayjs(from), dayjs(to)] : undefined,
      subsidiaryCompanyIds: getNumberListFromURLSearchParams(
        searchParams,
        SearchParamKeys.SubsidiaryCompanyIds,
      ),
      courseIds: getNumberListFromURLSearchParams(
        searchParams,
        SearchParamKeys.CourseIds,
      ),
      contactPersonIds: selectedContactPersonIds,
    });
  }, [form, searchParams, selectedContactPersonIds]);

  const updateSearchParams = (values: FilterFormValues) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      const [from, to] = values.date ?? [];

      if (from && to) {
        next.set(SearchParamKeys.From, from.format('YYYY-MM-DD'));
        next.set(SearchParamKeys.To, to.format('YYYY-MM-DD'));
      } else {
        next.delete(SearchParamKeys.From);
        next.delete(SearchParamKeys.To);
      }

      setNumberListInURLSearchParams(
        next,
        SearchParamKeys.SubsidiaryCompanyIds,
        values.subsidiaryCompanyIds,
      );
      setNumberListInURLSearchParams(
        next,
        SearchParamKeys.CourseIds,
        values.courseIds,
      );
      setNumberListInURLSearchParams(
        next,
        SearchParamKeys.ContactPersonIds,
        values.contactPersonIds,
      );

      return next;
    });
  };

  const resetFilter = () => {
    const values: FilterFormValues = {
      subsidiaryCompanyIds: defaultSubsidiaryCompanyId
        ? [defaultSubsidiaryCompanyId]
        : undefined,
    };
    form.setFieldsValue(values);
    updateSearchParams(values);
  };

  return (
    <aside className={className}>
      <Form<FilterFormValues>
        className="rounded-lg bg-white p-4 shadow-sm"
        form={form}
        layout="vertical"
        onValuesChange={(_, values) => updateSearchParams(values)}>
        <Form.Item label={t('common.date')} name="date">
          <RangePicker className="w-full" format="YYYY-MM-DD" />
        </Form.Item>
        {canFilterSubsidiaryCompanies && (
          <Form.Item
            label={t('common.subsidiaryCompanies')}
            name="subsidiaryCompanyIds">
            <Select
              allowClear
              mode="multiple"
              optionFilterProp="label"
              options={subsidiaryCompanies.map((company) => ({
                label: company.name,
                value: company.id,
              }))}
              placeholder={t('common.select')}
              showSearch
            />
          </Form.Item>
        )}
        {canReadCourses && (
          <Form.Item label={t('common.courses')} name="courseIds">
            <Select
              allowClear
              filterOption={(input, option) =>
                option?.props.children.props.course.name
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              mode="multiple"
              placeholder={t('common.select')}
              showSearch>
              {courses.map((course) => (
                <Select.Option key={course.id} value={course.id}>
                  <CourseOption course={course} user={currentUser} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {canReadContactPersons && (
          <Form.Item label={t('common.contactPerson')} name="contactPersonIds">
            <Select
              allowClear
              filterOption={false}
              loading={loadingContactPersons}
              mode="multiple"
              notFoundContent={
                loadingContactPersonOptions ? (
                  <div className="flex justify-center items-center w-full my-3">
                    <Spin />
                  </div>
                ) : undefined
              }
              onDropdownVisibleChange={(open) => {
                if (open) {
                  loadFirstContactPersonPage('');
                }
              }}
              onPopupScroll={(event) => {
                const target = event.target as HTMLDivElement;
                const isNearBottom =
                  target.scrollTop + target.offsetHeight >=
                  target.scrollHeight - 32;

                if (isNearBottom && hasMoreContactPersons) {
                  loadMoreContactPersons();
                }
              }}
              onSearch={searchContactPersons}
              options={contactPersons.map((contactPerson) => ({
                label: getFullContactName(contactPerson),
                value: contactPerson.id,
              }))}
              placeholder={t('common.select')}
              showSearch
            />
          </Form.Item>
        )}
        <Button
          block
          icon={<ReloadOutlined />}
          onClick={resetFilter}
          type="primary">
          {t('components.BookingOrderFilterPicker.resetFilter')}
        </Button>
      </Form>
    </aside>
  );
};

export default OpenClassBookingOrderFilterPicker;
